import { useEffect, useState } from "react";
import * as Tone from "tone";
import {
  MultiKeyInstrument,
  loadSampleAndExtractWaveform,
} from "../utils/sampleUtils";
import { createEnvelopedSampler, validateAudioUrl } from "../utils/audioUtils";

interface UseSampleEngineParams {
  currentInstrument: MultiKeyInstrument;
  setError: (error: string | null) => void;
}

export const useSampleEngine = ({
  currentInstrument,
  setError,
}: UseSampleEngineParams) => {
  const [sampler, setSampler] = useState<Tone.Sampler | null>(null);
  const [isSamplerLoaded, setIsSamplerLoaded] = useState<boolean>(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [sampleDuration, setSampleDuration] = useState<number>(1.0);

  useEffect(() => {
    let isDisposed = false;
    let createdSampler: Tone.Sampler | null = null;

    const loadAudioSamples = async () => {
      setIsSamplerLoaded(false);

      try {
        const firstSamplePath = Object.values(currentInstrument.samples)[0];
        console.log(`Loading audio samples for: ${firstSamplePath}`);

        try {
          await validateAudioUrl(firstSamplePath);
        } catch (validationError) {
          if (isDisposed) {
            return;
          }
          console.error("Audio URL validation failed:", validationError);
          setError(`Failed to access audio file: ${firstSamplePath}`);
          return;
        }

        createdSampler = createEnvelopedSampler(
          currentInstrument.samples,
          undefined,
          () => {
            if (isDisposed) {
              return;
            }
            console.log("Sampler loaded successfully");
            setIsSamplerLoaded(true);
            setError(null);
          },
          (error) => {
            if (isDisposed) {
              return;
            }
            console.error("Error loading sampler:", error);
            setIsSamplerLoaded(false);
            setError(`Failed to load sampler: ${error.message}`);
          },
        );

        if (isDisposed) {
          createdSampler.dispose();
          return;
        }

        setSampler(createdSampler);
        console.log("Audio samples loading started");
      } catch (err) {
        if (isDisposed) {
          return;
        }
        console.error("Error loading audio samples:", err);
        setError(
          `Failed to load audio samples: ${
            err instanceof Error ? err.message : "Unknown error"
          }`,
        );
        setIsSamplerLoaded(false);
      }
    };

    void loadAudioSamples();

    return () => {
      isDisposed = true;
      if (createdSampler) {
        createdSampler.dispose();
      }
    };
  }, [currentInstrument.samples, setError]);

  useEffect(() => {
    const firstSamplePath = Object.values(currentInstrument.samples)[0];
    loadSampleAndExtractWaveform(
      firstSamplePath,
      setWaveformData,
      setError,
    ).then((result) => {
      if (result) {
        setSampleDuration(result.duration);
      } else {
        setSampleDuration(1.0);
      }
    });
  }, [currentInstrument, setError]);

  return {
    sampler,
    isSamplerLoaded,
    waveformData,
    sampleDuration,
  };
};
